import { throwError as observableThrowError, Observable, BehaviorSubject } from 'rxjs';
import { catchError, take, filter, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpEventType } from '@angular/common/http';
import { LocalStorageService, SpinnerService } from '../xform-compat';
import { EventsHubService } from './events-hub.service';
import { ErrorHandlerGlobalTrackerService, UserService } from './data-services';
import { AuthenticationService, PermissionsChangedLogoutService } from './services';
import { User } from '../shared/models';
import { CONFIG } from '../config';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  // A subject for holding the next access token value
  private accessTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private intervalId = 0;

  constructor(
    private spinnerService: SpinnerService,
    private eventHub: EventsHubService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    public authService: AuthenticationService,
    private errorHandlerGlobalTrackerService: ErrorHandlerGlobalTrackerService,
    private authenticationService: AuthenticationService,
    private permissionsChangedLogoutService: PermissionsChangedLogoutService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authRequest = this.addAuthorizationHeader(req);

    return next.handle(authRequest).pipe(
      catchError(err => {
        this.notifyServerOfError(req, err);

        if ((err instanceof HttpErrorResponse || HttpEventType.Sent) && err.status === 401) {

          const duoError = (err.error.code === CONFIG.twoFactorAuthErrorCode);

          const token = this.localStorageService.get('tokenInfo');

          if (err.url.includes('oauth/token') || err.url.includes('token-proxy/token')) {
            if (duoError) {
              return observableThrowError(err);
            } else {
              return this.logout(err);
            }
          }

          // refresh token and send request again
          if (token) {
            return this.refreshTokenAndRetryRequest(authRequest, next);
          }

          return this.logout(err);

        } else {
          this.spinnerService.stop();
          this.eventHub.setUnauthorizedError(false);
        }

        return observableThrowError(err);
      })
    );
  }

  private notifyServerOfError(req, err) {
    const oldLoggedUserInfo = this.localStorageService.get('loggedUser');
    if (CONFIG.enableServerEmailLogging && err.status !== 500 && err.status !== 401 && err.status !== 0
      && !err.error.authCode && !err.url.includes('notifications/notifyerror')) {
      // tracking all global errors
      const error = new Error(err.message);
      const sourceUrl = `${req.method} - ${err.url}`;
      let stackTrace = '';
      let params = req.params ? req.params.toString() : '';
      if (req.body) {
        params = `${params}${!!params && '\n'}Body: ${req.body.toString()}`;
      }
      if (params) {
        stackTrace = `Params: ${params}`;
      }
      if (oldLoggedUserInfo) {
        stackTrace = `Triggered by ${oldLoggedUserInfo.fullName}\n${stackTrace}`;
      }

      this.errorHandlerGlobalTrackerService.trackErrorException({
        sourceUrl,
        message: err.message,
        stackTrace
      });
    }
  }

  private addAuthorizationHeader(request: HttpRequest<any>): HttpRequest<any> {

    const tokenInfo = this.localStorageService.get('tokenInfo');
    const url = request.url;
    const authRequired = (url.includes('/api')) && (!url.includes('/signin-redirect'));

    if (authRequired) {
      if (tokenInfo) {
        const req = request.clone({
          headers: request.headers.set('Authorization', `Bearer ${tokenInfo.accessToken}`)
        });
        if (req.url === '/api/uaa/authorization/logout') {
          this.authService.clearLocalStorage();
        }
        return req;
      }
    }
    return request;
  }

  private refreshTokenAndRetryRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // We want to refresh the access token.
    // Check to see if another web browser tab is currently refreshing the access token.  We do this by
    // attempting to reserve the right to refresh the access token.
    if (this.localStorageService.reserveToRefreshToken()) {
      // This web browser tab has reserved the right to refresh the access token.
      // Refresh the access token locally, here, and set the next value of the accessTokenSubject..
      // Null out the value of the accessTokenSubject so other listeners know it's not there
      this.accessTokenSubject.next(null);

      const oldLoggedUserInfo = this.localStorageService.get('loggedUser');

      return this.authService.refreshToken(this.localStorageService.get('tokenInfo')).pipe(
        switchMap((token) => {
          this.checkForPermissionsChanges(oldLoggedUserInfo);
          // Set the value of the access token for other clients in this tab
          this.accessTokenSubject.next(token);
          // Stop refreshing the access token.  Other tabs will be able to access the access token
          this.localStorageService.refreshTokenFinished();
          if (token) {
            // repeat failed request with new token
            return next.handle(this.addAuthorizationHeader(request));
          }
          return this.logout('');
        })
      );
    } else {
      // Another web browser tab (or another caller of this method in the current tab) is refreshing the
      // access token and we will have to periodically check if that refresh is done.
      if (this.intervalId === 0) {
        // Create an interval that periodically checks local storage to see if the access token is still being
        // refreshed.  When the token is no longer being refreshed, get the refreshed token info and clear
        // the interval.
        // This case ensures that this web browser tab is listening for another tab to refresh the access token.
        let intervals = 0;
        const func = () => {
          let clear = false;
          if (!this.localStorageService.isRefreshingToken()) {
            // The access token refresh is done.
            // Get the access token, set its value in the accessTokenSubject and clear the interval.
            const token = this.localStorageService.get('tokenInfo');
            this.accessTokenSubject.next(token);
            clear = true;
          } else {
            // Don't wait forever for the token to refresh.  Only wait 10 times max.
            intervals++;
            if (intervals >= 10) {
              clear = true;
            }
          }
          if (clear) {
            clearInterval(this.intervalId);
            this.intervalId = 0;
            // We are also done waiting for the refresh token
            this.localStorageService.refreshTokenFinished();
          }
          intervals++;
        };
        // @ts-ignore
        this.intervalId = setInterval(func, 300);
      }

      // At this point, there is an interval waiting for the refreshed value from local storage.
      // Just wait for the accessTokenSubject to have a non-null value, and use it.
      return this.accessTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(() =>
          // repeat failed request with new token
           next.handle(this.addAuthorizationHeader(request))
        )
      );
    }
  }


  private logout(err): Observable<HttpEvent<any>> {
    this.localStorageService.refreshTokenFinished();
    this.authService.logout();
    this.eventHub.stopHeartBeat();
    this.spinnerService.stop();

    return observableThrowError(err);
  }

  /**
   * Refresh the stored logged user after a token refresh, and log the user out after a countdown if
   * their permissions changed.
   */
  checkForPermissionsChanges(oldLoggedUserInfo: User | null): void {
    this.userService.getLoggedUser().subscribe((newLoggedUserInfo: User) => {
      if (!newLoggedUserInfo) {
        return;
      }
      // Always persist. Every permission check reads exclusively from the stored loggedUser, so
      // without this a reload would re-read the stale copy and newly granted permissions would only
      // take effect after a full re-login. This also repopulates the stored user when only tokenInfo
      // is present, as happens when a session is restored from the iOS cookie bridge.
      this.authenticationService.setLoggedUser(newLoggedUserInfo);

      // Only prompt when there was a stored copy to compare against. Mid-login the token is stored
      // before the user is, and permissionsChanged treats a missing old copy as a change, which
      // would fire a spurious logout on every fresh login.
      if (!oldLoggedUserInfo || !this.authenticationService.permissionsChanged(oldLoggedUserInfo, newLoggedUserInfo)) {
        return;
      }

      // Forced logout (not a reload) so a page the user lost access to, or one that gained a feature,
      // is never left open on stale permissions (RA-2262)
      this.permissionsChangedLogoutService.promptAndLogout();
    }, () => {});
  }
}
